import React from "react";
import { useTranslation } from "react-i18next";
import { ProgressTotals } from "../../hooks/useProgressData";
import DonutGauge from "../charts/DonutGauge";
import LinearBar from "../charts/LinearBar";
import ProgressOverviewSkeleton from "./skeletons/ProgressOverviewSkeleton";

interface ProgressOverviewProps {
  totals: ProgressTotals;
  remaining: number;
  overallPct: number;
  actualPct: number;
  remainingPct: number;
  devPct: number;
  isLoading?: boolean;
}

const ProgressOverview: React.FC<ProgressOverviewProps> = ({
  totals,
  remaining,
  overallPct,
  actualPct,
  remainingPct,
  devPct,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  if (isLoading) {
    return <ProgressOverviewSkeleton />;
  }
  return (
    <div className="grid gap-6 rounded-xl border border-slate-200 bg-slate-50 p-5 md:grid-cols-[1.4fr_1fr]">
      <div>
        <h2 className="mb-3 text-xl font-bold text-slate-900">{t("dashboard_reports.planned_days_progress")}</h2>
        <div className="flex gap-10">
          <div>
            <div className="text-4xl font-extrabold text-slate-900">{totals.installed}</div>
            <div className="text-slate-700">{t("dashboard_reports.actual_planned")}</div>
          </div>
          <div>
            <div className="text-4xl font-extrabold text-teal-700">{remaining}</div>
            <div className="text-slate-700">{t("dashboard_reports.remaining_planned")}</div>
          </div>
          <div>
            <div className="text-4xl font-extrabold text-rose-700">
              {totals.real_vs_plan_dev}
            </div>
            <div className="text-slate-700">{t("dashboard_reports.real_vs_plan_dev")}</div>
          </div>
        </div>

        <div className="mt-5 grid max-w-xl gap-5">
          <LinearBar
            percent={actualPct}
            barClass="bg-emerald-500"
            trackClass="bg-slate-200"
            heightClass="h-3"
            label={
              <div className="flex items-center justify-between">
                <span className="text-slate-800">{t("dashboard_reports.actual")}</span>
                <span className="font-semibold text-slate-900">
                  {Math.round(actualPct)}%
                </span>
              </div>
            }
          />
          <LinearBar
            percent={remainingPct}
            barClass="bg-teal-600"
            trackClass="bg-slate-200"
            heightClass="h-3"
            label={
              <div className="flex items-center justify-between">
                <span className="text-slate-800">{t("dashboard_reports.remaining")}</span>
                <span className="font-semibold text-slate-900">
                  {Math.round(remainingPct)}%
                </span>
              </div>
            }
          />
          <LinearBar
            percent={devPct}
            barClass="bg-rose-500"
            trackClass="bg-slate-200"
            heightClass="h-3"
            label={
              <div className="flex items-center justify-between">
                <span className="text-slate-800">{t("dashboard_reports.real_vs_plan_dev_short")}</span>
                <span className="font-semibold text-slate-900">
                  {Math.round(devPct)}%
                </span>
              </div>
            }
          />
        </div>
      </div>

      <div className="grid justify-items-center">
        <div className="text-lg font-bold text-slate-900">Overall % Installed</div>
        <DonutGauge percent={overallPct} text={`${overallPct}%`} />
      </div>
    </div>
  );
};

export default ProgressOverview;
